import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { CustomDateAdapter } from '../../../shared/services/custom-date-adapter';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Subject, takeUntil } from 'rxjs';

import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { MovimentacaoService } from '../../../shared/services/apis/financas/movimentacao.service';
import { TransferenciaService } from '../../../shared/services/apis/financas/transferencia.service';
import { MaskService } from '../../../shared/services/mask.service';
import { ContaFinanceiraListItem } from '../../../shared/services/structs/financas/conta-financeira-list-item.struct';

interface SelectOption {
    id: number;
    descricao: string;
    saldoAtual?: number;
    disabled?: boolean;
}

@Component({
    selector: 'app-transferencia-cadastro',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatDatepickerModule,
        MatNativeDateModule,
        NgxCurrencyDirective,
        NgxEditorModule,
        HeaderComponent,
        LoadingBoxComponent,
        FooterActionComponent,
        SelectPesquisavelComponent
    ],
    templateUrl: './transferencia-cadastro.component.html',
    styleUrl: './transferencia-cadastro.component.css',
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
        { provide: DateAdapter, useClass: CustomDateAdapter },
    ]
})
export class TransferenciaCadastroComponent implements OnInit, OnDestroy {
    public moduloEnum = ModuloEnum.Financas;
    public funcionalidadeEnum = FuncionalidadeEnum.Financas_Extrato;

    public form!: FormGroup;
    public isLoading = false;
    public isSaving = false;
    public editor!: Editor;
    public currencyMask = this.maskService.getDynamicCurrencyMask();
    public contaOptions: SelectOption[] = [];

    public toolbar: Toolbar = [
        ['bold', 'italic'],
        ['underline', 'strike'],
        ['ordered_list', 'bullet_list'],
        [{ heading: ['h1', 'h2', 'h3'] }],
        ['text_color', 'background_color'],
        ['align_left', 'align_center', 'align_right', 'align_justify'],
        ['link']
    ];

    private _idTransferencia: number | null = null;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private formBuilder: FormBuilder,
        private router: Router,
        private route: ActivatedRoute,
        private alertService: AlertService,
        private movimentacaoService: MovimentacaoService,
        private transferenciaService: TransferenciaService,
        private maskService: MaskService
    ) { }

    ngOnInit(): void {
        this.editor = new Editor();
        this.createForm();
        this.configureReactiveBehavior();

        const id = this.route.snapshot.paramMap.get('id');
        this._idTransferencia = id ? Number(id) : null;

        this.loadLookups();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.editor.destroy();
    }

    public get idContaOrigemControl(): FormControl {
        return this.form.get('idContaOrigem') as FormControl;
    }

    public get idContaDestinoControl(): FormControl {
        return this.form.get('idContaDestino') as FormControl;
    }

    public get contaDestinoOptions(): SelectOption[] {
        const contaOrigem = Number(this.idContaOrigemControl.value || 0);
        return this.contaOptions.map(item => ({
            ...item,
            disabled: contaOrigem > 0 && item.id === contaOrigem
        }));
    }

    public get saldoContaOrigem(): number | null {
        const id = Number(this.idContaOrigemControl.value || 0);
        return id > 0 ? (this.contaOptions.find(c => c.id === id)?.saldoAtual ?? null) : null;
    }

    public get saldoContaDestino(): number | null {
        const id = Number(this.idContaDestinoControl.value || 0);
        return id > 0 ? (this.contaOptions.find(c => c.id === id)?.saldoAtual ?? null) : null;
    }

    public get nomeContaOrigem(): string {
        const id = Number(this.idContaOrigemControl.value || 0);
        return this.contaOptions.find(c => c.id === id)?.descricao ?? '';
    }

    public get nomeContaDestino(): string {
        const id = Number(this.idContaDestinoControl.value || 0);
        return this.contaOptions.find(c => c.id === id)?.descricao ?? '';
    }

    public voltar(): void {
        this.router.navigate(['/financas/extrato-movimentacoes']);
    }

    public salvar(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.alertService.show('Atenção', 'Preencha os campos obrigatórios da transferência.', AlertType.warning);
            return;
        }

        if (Number(this.idContaOrigemControl.value) === Number(this.idContaDestinoControl.value)) {
            this.alertService.show('Atenção', 'A conta de origem e a conta de destino devem ser diferentes.', AlertType.warning);
            return;
        }

        const raw = this.form.getRawValue();
        const request = {
            dataTransferencia: (raw.data as Date).toISOString(),
            idContaFinanceiraOrigem: Number(raw.idContaOrigem),
            idContaFinanceiraDestino: Number(raw.idContaDestino),
            valor: raw.valor,
            descricao: raw.historicoHtml || null
        };

        this.isSaving = true;

        const call$ = this._idTransferencia
            ? this.transferenciaService.update(this._idTransferencia, request)
            : this.transferenciaService.create(request);

        call$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
                this.isSaving = false;
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription || 'Não foi possível salvar a transferência.', AlertType.error);
                    return;
                }
                this.alertService.show('Sucesso', this._idTransferencia
                    ? 'Transferência atualizada com sucesso.'
                    : 'Transferência realizada com sucesso.', AlertType.success);
                this.router.navigate(['/financas/extrato-movimentacoes']);
            },
            error: () => {
                this.isSaving = false;
                this.alertService.show('Erro', 'Não foi possível salvar a transferência.', AlertType.error);
            }
        });
    }

    private createForm(): void {
        this.form = this.formBuilder.group({
            data: [new Date(), Validators.required],
            valor: [null, [Validators.required, Validators.min(0.01)]],
            idContaOrigem: [null, Validators.required],
            idContaDestino: [null, Validators.required],
            historicoHtml: ['<p>Transferência entre contas</p>', Validators.required]
        });
    }

    private configureReactiveBehavior(): void {
        this.idContaOrigemControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => {
                if (value && Number(value) === Number(this.idContaDestinoControl.value)) {
                    this.idContaDestinoControl.setValue(null);
                }
            });
    }

    private loadLookups(): void {
        this.isLoading = true;

        this.movimentacaoService.getLookup()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => {
                    if (response.isError) {
                        this.isLoading = false;
                        this.alertService.show('Erro', response.errorDescription || 'Não foi possível carregar as contas da transferência.', AlertType.error);
                        return;
                    }

                    const contas = (response.listaContaFinanceira || []).filter(item => item.isAtivo);
                    this.contaOptions = contas.map(item => ({
                        id: item.idContaFinanceira ?? 0,
                        descricao: item.descricao,
                        saldoAtual: item.saldoAtual
                    }));

                    if (this._idTransferencia) {
                        this.carregarTransferencia(this._idTransferencia);
                    } else {
                        this.isLoading = false;
                        const contaOrigemPadrao = response.idContaFinanceiraSelecionada
                            ?? this.getContaCaixaPadraoId(contas)
                            ?? null;
                        this.form.patchValue({ idContaOrigem: contaOrigemPadrao }, { emitEvent: false });
                    }
                },
                error: () => {
                    this.isLoading = false;
                    this.alertService.show('Erro', 'Não foi possível carregar as contas da transferência.', AlertType.error);
                }
            });
    }

    onDateInput(event: Event): void {
        MaskService.onDateInput(event);
    }

    private carregarTransferencia(id: number): void {
        this.transferenciaService.getById(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.isLoading = false;
                    if (response.isError || !response.transferencia) {
                        this.alertService.show('Erro', response.errorDescription || 'Transferência não encontrada.', AlertType.error);
                        this.router.navigate(['/financas/extrato-movimentacoes']);
                        return;
                    }
                    const t = response.transferencia;
                    this.form.patchValue({
                        data: new Date(t.dataTransferencia),
                        valor: t.valor,
                        idContaOrigem: t.idContaFinanceiraOrigem,
                        idContaDestino: t.idContaFinanceiraDestino,
                        historicoHtml: t.descricao || ''
                    }, { emitEvent: false });
                },
                error: () => {
                    this.isLoading = false;
                    this.alertService.show('Erro', 'Não foi possível carregar a transferência.', AlertType.error);
                    this.router.navigate(['/financas/extrato-movimentacoes']);
                }
            });
    }

    private getContaCaixaPadraoId(contas: ContaFinanceiraListItem[]): number | null {
        const normalize = (value?: string | null) => (value || '').trim().toLowerCase();
        const isContaCaixa = (item: ContaFinanceiraListItem) =>
            normalize(item.tipoConta).includes('caixa') || normalize(item.descricao).includes('caixa');

        return contas.find(item => isContaCaixa(item) && item.isPreferencial)?.idContaFinanceira
            ?? contas.find(item => isContaCaixa(item) && item.isPadrao)?.idContaFinanceira
            ?? contas.find(item => normalize(item.descricao) === 'caixa')?.idContaFinanceira
            ?? contas.find(item => isContaCaixa(item))?.idContaFinanceira
            ?? contas.find(item => item.isPreferencial)?.idContaFinanceira
            ?? contas.find(item => item.isPadrao)?.idContaFinanceira
            ?? contas[0]?.idContaFinanceira
            ?? null;
    }
}
